<template>
    <header class="navbar">
        <div class="container">
            <div class="navbar-content">
                <div class="navbar-brand">
                    <router-link to="/">
                        <img src="/avatar.png" alt="Ugly Avatar Logo" class="logo" />
                        <span>Ugly Avatar</span>
                    </router-link>
                </div>

                <div class="navbar-menu" :class="{ active: menuOpen }">
                    <router-link to="/" class="nav-link" exact-active-class="active">{{ $t('navbar.home')
                        }}</router-link>
                    <router-link to="/generator" class="nav-link" active-class="active">{{ $t('navbar.createAvatar')
                        }}</router-link>
                    <router-link to="/gallery" class="nav-link" active-class="active">{{ $t('navbar.myCollection')
                        }}</router-link>

                    <div class="language-selector">
                        <button @click="toggleLanguageMenu" class="lang-btn" title="Change language">
                            <span class="lang-icon">🌐</span>
                            <span class="lang-code">{{ currentLocale.toUpperCase() }}</span>
                            <span class="lang-arrow">▼</span>
                        </button>
                        <div class="language-menu" v-if="showLanguageMenu">
                            <button v-for="locale in availableLocales" :key="locale.code"
                                @click="changeLanguage(locale.code)" class="lang-option"
                                :class="{ active: currentLocale === locale.code }">
                                <span class="lang-flag">{{ locale.flag }}</span>
                                <span class="lang-name">{{ locale.name }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <button class="mobile-toggle" @click="toggleMenu" aria-label="Toggle navigation menu">
                    <span></span>
                    <span></span>
                    <span></span>
                </button>
            </div>
        </div>
    </header>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';

export default {
    name: 'NavBar',
    setup() {
        const { locale } = useI18n();
        const router = useRouter();
        const route = useRoute();
        const showLanguageMenu = ref(false);

        const availableLocales = [
            { code: 'en', name: 'English', flag: '🇺🇸' },
            { code: 'zh', name: '中文', flag: '🇨🇳' },
            { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
            // 东南亚语言
            { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
            { code: 'th', name: 'ไทย', flag: '🇹🇭' },
            { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
            { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
        ];

        const currentLocale = computed(() => locale.value);

        const changeLanguage = (localeCode) => {
            // 设置语言，URL会在App.vue中的watch中处理
            locale.value = localeCode;
            showLanguageMenu.value = false;
        };

        const toggleLanguageMenu = () => {
            showLanguageMenu.value = !showLanguageMenu.value;
        };

        // 点击外部关闭语言菜单
        document.addEventListener('click', (e) => {
            const langSelector = document.querySelector('.language-selector');
            if (langSelector && !langSelector.contains(e.target)) {
                showLanguageMenu.value = false;
            }
        });

        return {
            currentLocale,
            availableLocales,
            showLanguageMenu,
            changeLanguage,
            toggleLanguageMenu
        };
    },
    data() {
        return {
            menuOpen: false
        }
    },
    methods: {
        toggleMenu() {
            this.menuOpen = !this.menuOpen;
        },
        closeMenu() {
            this.menuOpen = false;
        }
    },
    watch: {
        $route() {
            this.closeMenu();
        }
    }
}
</script>

<style scoped>
.navbar {
    width: 100%;
    background-color: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 100;
    padding: 0.8rem 0;
}

.navbar-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.navbar-brand a {
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 1.4rem;
    color: var(--secondary-color);
}

.logo {
    height: 36px;
    width: auto;
    margin-right: 10px;
}

.navbar-menu {
    display: flex;
    gap: 1.5rem;
    align-items: center;
}

.nav-link {
    color: var(--text-secondary);
    font-weight: 500;
    padding: 0.5rem 0.75rem;
    border-radius: var(--border-radius-sm);
    position: relative;
}

.nav-link.active {
    color: var(--primary-color);
}

.nav-link.active::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 3px;
    background-color: var(--primary-color);
    border-radius: 2px;
}

.mobile-toggle {
    display: none;
    flex-direction: column;
    justify-content: space-between;
    width: 30px;
    height: 21px;
    background: transparent;
    border: none;
    padding: 0;
}

.mobile-toggle span {
    display: block;
    height: 3px;
    width: 100%;
    background-color: var(--secondary-color);
    border-radius: 3px;
    transition: var(--transition);
}

/* 语言选择器样式 */
.language-selector {
    position: relative;
    z-index: 101;
}

.lang-btn {
    display: flex;
    align-items: center;
    background: transparent;
    border: 1px solid #eee;
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.lang-btn:hover {
    background-color: #f5f5f5;
}

.lang-icon {
    margin-right: 6px;
    font-size: 16px;
}

.lang-code {
    font-weight: 500;
    margin-right: 6px;
}

.lang-arrow {
    font-size: 10px;
    color: #666;
}

.language-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border-radius: 4px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    min-width: 150px;
    margin-top: 5px;
    overflow: hidden;
    animation: slideDown 0.2s ease;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.lang-option {
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    padding: 10px 15px;
    border: none;
    background: white;
    cursor: pointer;
    transition: background 0.2s ease;
}

.lang-option:hover {
    background-color: #f5f5f5;
}

.lang-option.active {
    background-color: #f0f7ff;
    color: var(--primary-color);
}

.lang-flag {
    margin-right: 8px;
    font-size: 16px;
}

.lang-name {
    font-size: 14px;
}

@media (max-width: 768px) {
    .mobile-toggle {
        display: flex;
    }

    .navbar-menu {
        position: fixed;
        top: 60px;
        left: 0;
        width: 100%;
        background-color: white;
        flex-direction: column;
        padding: 1.5rem;
        transform: translateY(-100%);
        opacity: 0;
        pointer-events: none;
        transition: var(--transition);
        box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
        text-align: center;
        align-items: center;
    }

    .navbar-menu.active {
        transform: translateY(0);
        opacity: 1;
        pointer-events: all;
    }

    .navbar-menu .nav-link {
        padding: 1rem 0.75rem;
        width: 100%;
    }

    .language-selector {
        margin-top: 1rem;
    }

    .lang-code {
        display: none;
    }
}
</style>
